import * as triggerConfigs from "src/config/triggerConfigs.json";
import * as solutionConfig from "src/config/solutionConfigs.json";
import {CommonConstants, PRESENTATION_DETAIL_PREFIX, Regex, StringUtils} from "src/constant/constants";
import {logger} from "src/logger";
import {EventModelState} from "src/state/slice/EventModelSlice";
import axios from "axios";
import axiosRetry from "axios-retry";

export const getStage = () => {
  return "alpha"
}

/**
 * Returns the alias of the logged in user.
 */
export const getLoggedInUserAlias = (): string => {
  return window?.name ?? "";
}

/**
 * Returns useCase for a particular trigger passed.
 */
export const getUseCaseForTrigger = (trigger: string): string => {
  for (const triggerConfig of Object.entries(triggerConfigs)) {
    if (triggerConfig[1]?.triggerName === trigger) {
      return triggerConfig[0];
    }
  }
  return "";
}

/**
 * Returns trigger name for a particular useCase passed.
 */
export const getTriggerForUseCase = (useCase: string): string => {
  return triggerConfigs[`${useCase}`]?.triggerName ?? "";
}

/**
 * Returns if an MCM URL is valid.
 */
export const isMcmUrlValid = (mcmUrl: string): boolean => {
  return mcmUrl?.startsWith(CommonConstants.MCM_URL_PREFIX);
}

export const isUserEligibleForProdPromotion = (): boolean => {
  return ["aapatni", "aayushjd", "nikhiary", "agarmah", "nkshah", "sivapadm", "appubill", "klodhi", "pranije", "nikhilgd",
    "pgauravq", "rakshakr", "srajitsr","vardaanb", "saaketn", "akashwa", "akkinag"]
    .includes(getLoggedInUserAlias());
}

export const isNullOrUndefined = (input: any): boolean => {
  return input == null || input == undefined;
}

/**
 * returns if the string input is null or undefined or has an empty string
 * @param input is the input string
 */
export const isNullOrUndefinedOrEmpty = (input: string): boolean => {
  return isNullOrUndefined(input) || input === StringUtils.EMPTY;
}
/**
 * extract date values from epoch timestamp
 * @param isoString is the epoch timestamp
 * @return date value in MM/DD/YYYY:HH:MM:SS
 */
export const convertIsoStringToDateTime = (isoString: string): string => {
  const timestamp = parseInt(isoString);
  const date = new Date(timestamp);

  const year = date.getFullYear();
  const month = ('0' + (date.getMonth() + 1)).slice(-2); // Adding 1 to month because it is zero-indexed
  const day = ('0' + date.getDate()).slice(-2);
  const hours = ('0' + date.getHours()).slice(-2);
  const minutes = ('0' + date.getMinutes()).slice(-2);
  const seconds = ('0' + date.getSeconds()).slice(-2);

  return `${month}-${day}-${year}:${hours}:${minutes}:${seconds}`;
}

/**
 * Method to convert file to base64
 * @param file
 */
export const readFileAsBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result.split(StringUtils.COMMA_SEPARATOR)[1]);
        reader.abort();
      }
    };
    reader.onerror = (error) => {
      reject(error);
      reader.abort();
    };
    reader.onabort = () => {
      logger.error('FileReader aborted');
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Returns all required fields from solutionConfigs for a particular solutionType
 * @param solutionType is the solution type for which required fields need to be returned
 * @returns mandatory solution attributes for a solution type
 */
export const mandatorySolutionAttributes = (solutionType: string): string[] => {
  let requiredFields: string[] = [];
  solutionConfig[solutionType]?.solutionDetailAttributes.forEach(solutionAttribute => {
    if (solutionAttribute.isRequired) {
      requiredFields.push(solutionAttribute.attributeName);
    }
  });
  return requiredFields;
}

/**
 * getIsInlineAuthoringEnabled: return if inlineAuthoring Enabled for a solution type or not.
 * @param solutionType
 */
export const getIsInlineAuthoringEnabled = (solutionType: string): boolean => {
  return solutionConfig[solutionType]?.isInlineAuthoring;
}

/**
 * Returns true if solution details object contains all required inputs. Returns false otherwise.
 * @param eventModelState the state of the event model containing the solution type
 * @param requiredSolutionAttributes the required attributes for the solution details object
 */
export const isValidateSolutionDetailsInput = (eventModelState: EventModelState, requiredSolutionAttributes: string[]): boolean => {
  // TODO: add validation for presentation detail attributes
  let isValidSolutionAttributesInput: boolean = true;
  for (const attribute of requiredSolutionAttributes) {
    if (attribute.startsWith(PRESENTATION_DETAIL_PREFIX)) {
      return true;
    }
    else if(typeof eventModelState?.eventSolution?.solutionDetailObject[attribute] == "boolean") {
      return true;
    }
    else if(Regex.EmptyPTagRegex.test(eventModelState?.eventSolution?.solutionDetailObject[attribute]?.trim())){
      isValidSolutionAttributesInput = false;
      break;
    }
    else if (isNullOrUndefinedOrEmpty(eventModelState?.eventSolution?.solutionDetailObject[attribute]?.trim())) {
      isValidSolutionAttributesInput = false;
      break;
    }
  }
  return isValidSolutionAttributesInput;
}

export const isValidJson = (value: string) :boolean => {
  if(!isNullOrUndefinedOrEmpty(value)) {
    try {
      JSON.parse(value);
    } catch (e) {
      return false;
    }
    return true;
  }
}

export const isValidEventRule = (eventRule: string) : boolean => {
  let isValidEventRule = true;
  if(!isNullOrUndefined(eventRule)) {
    if (Regex.EmptyBracesRegex.test(eventRule)) {
      return false;
    }

    const inRules = eventRule?.match(Regex.InRuleRegex);
    if(eventRule?.match(Regex.InRuleRegex) != null){
      for(const rule of inRules){
        const jsonData = JSON.parse(rule);
        const values = jsonData.in[1];
        if(!isNullOrUndefined(values) && !values["var"]){
          if(!Array.isArray(values)){
            return false;
          }
        }
      }
    }

    const equalToRules = eventRule?.match(Regex.EqualToRuleRegex);
    if(equalToRules != null){
      for(const rule of equalToRules){
        const jsonData = JSON.parse(rule);
        const values = jsonData["=="][1];
        if(!isNullOrUndefined(values) && !values["var"]){
          const specialCharRegex = /[\[\],]/g;
          if(specialCharRegex.test(values) || Array.isArray(values)){
            return false;
          }
        }
      }
    }
  }
  return isValidEventRule;
}

/**
 * AEMClient
 * @param requestConfig
 * @param data
 * @constructor
 */
export const AEMClient = async (requestConfig, data) => {
  axiosRetry(axios, {
    retries: 3, // number of retries
    retryDelay: (retryCount) => {
      console.log(`retry attempt: ${retryCount}`);
      return retryCount * 3000; // time interval between retries
    },
    retryCondition: (error) => {
      // if retry condition is not specified, by default idempotent requests are retried
      return error.response.status === 401;
    },
  });
  const config = {
    ...requestConfig,
    data: data,
    withCredentials: false,
  };
  config.headers = {
    'Content-Type': 'multipart/form-data',
  };
  return axios.request(config);
}

/**
 * createFormInput: Create Form Input
 * @param input
 */
export const createFormInput = (input) => {
  const formData = new FormData();
  formData.append("title", input.title);
  formData.append("topic",input.topic);
  formData.append("language","en");
  formData.append("contentType", input.contentType);
  formData.append("contentDetails", input.contentDetails);
  formData.append("client", input.client);
  formData.append("fieldNames", input.fieldNames);
  return formData;
}

